import { SetStateAction, Dispatch, useRef, useState } from "react";
import {
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Text,
  Box,
} from "@chakra-ui/react";
import { useMediaQuery } from "react-responsive";
import NestedTableRow from "./NestedTableRow";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";

const Table = ({
  columns,
  emptyMessage = "Nenhum dado encontrado",
  nestedColumns,
  page,
  onCellEdit,
  financial = false,
  isProcess = false,
  onSort,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection,
  reorderable = false,
  onColumnReorder,
}: {
  columns: {
    label: string;
    index?: string;
    data: any;
    width?: string[] | string | number;
    sortable?: boolean;
    fixed?: boolean;
  }[];
  onCellEdit?: (rowId: any, index: string, newValue: any) => Promise<void>;
  nestedColumns?: {
    label: string;
    index?: string;
    data: any;
    width?: string[] | string | number;
  }[];
  sortField?: string | undefined;
  setSortField?: Dispatch<SetStateAction<string | undefined>>;
  sortDirection?: "asc" | "desc";
  setSortDirection?: Dispatch<SetStateAction<"asc" | "desc">>;
  page: any[];
  financial?: boolean;
  isProcess?: boolean;
  emptyMessage: string;
  onSort: (field: string, direction: "asc" | "desc") => void;
  reorderable?: boolean;
  onColumnReorder?: (fromLabel: string, toLabel: string) => void;
}) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const dragLabelRef = useRef<string | null>(null);
  const didDragRef = useRef(false);
  const [dragOverLabel, setDragOverLabel] = useState<string | null>(null);

  const handleHeaderClick = (field: string) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (sortField === field) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";

      onSort(sortDirection === "desc" ? "" : field, newDirection);
      setSortDirection && setSortDirection(newDirection);
    } else {
      setSortField && setSortField(field);
      setSortDirection && setSortDirection("asc");
      onSort(field, "asc");
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return <FaSort />;
    return sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <ChakraTable variant="simple" fontFamily="Inter">
      <Thead>
        <Tr>
          {columns.map((column, idx) => {
            const canDrag = reorderable && !column.fixed && column.label.length > 0;
            return (
              <Th
                onClick={() =>
                  column.sortable ? handleHeaderClick(column.index ?? "") : null
                }
                cursor={column.sortable ? "pointer" : "default"}
                position="sticky"
                backgroundColor="white"
                top="0"
                left={idx === 0 ? "0" : undefined}
                right={column.label.length === 0 ? 0 : undefined}
                zIndex={column.label.length === 0 || idx === 0 ? "10" : "1"}
                textTransform="none"
                key={idx}
                width={column.width}
                _hover={{ bg: column.sortable ? "gray.50" : "inherit" }}
                draggable={canDrag}
                borderLeft={
                  canDrag && dragOverLabel === column.label
                    ? "2px solid var(--icon-color)"
                    : undefined
                }
                onDragStart={
                  canDrag
                    ? (e) => {
                        dragLabelRef.current = column.label;
                        e.dataTransfer.effectAllowed = "move";
                      }
                    : undefined
                }
                onDragOver={
                  canDrag
                    ? (e) => {
                        e.preventDefault();
                        if (dragOverLabel !== column.label)
                          setDragOverLabel(column.label);
                      }
                    : undefined
                }
                onDragLeave={
                  canDrag
                    ? () => {
                        setDragOverLabel((prev) =>
                          prev === column.label ? null : prev,
                        );
                      }
                    : undefined
                }
                onDrop={
                  canDrag
                    ? (e) => {
                        e.preventDefault();
                        setDragOverLabel(null);
                        const from = dragLabelRef.current;
                        dragLabelRef.current = null;
                        didDragRef.current = true;
                        if (from && from !== column.label) {
                          onColumnReorder?.(from, column.label);
                        }
                      }
                    : undefined
                }
                onDragEnd={
                  canDrag
                    ? () => {
                        dragLabelRef.current = null;
                        setDragOverLabel(null);
                      }
                    : undefined
                }
              >
                <Flex alignItems="center" gap="8px">
                  {column.label}
                  {column.sortable && (
                    <Box>{renderSortIcon(column.index ?? "")}</Box>
                  )}
                </Flex>
              </Th>
            );
          })}
        </Tr>
      </Thead>
      <Tbody>
        {page.length == 0 ? (
          <Tr>
            <Td colSpan={columns.length} py="32px" color="gray.500">
              {emptyMessage}
            </Td>
          </Tr>
        ) : (
          page.map((row, idx) => (
            <NestedTableRow
              key={row.id ?? idx}
              {...{
                onCellEdit,
                isProcess,
                financial,
                row,
                columns,
                nestedColumns,
                isMobile,
              }}
            />
          ))
        )}
      </Tbody>
    </ChakraTable>
  );
};

export default Table;
